import { LoggerService } from '@angular-ru/cdk/logger';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { cloneDeep, uniqueId } from 'lodash-es';
import { delay, filter, map, switchMap, take } from 'rxjs';
import { IMedia } from 'src/app/api/modules/core/dynamic/components/IMedia';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaManagerRepository } from '../../../services/media-manager/media-manager.repository';
import { MediaNativeAudioModel } from './media-native-audio/media-native-audio.model';
import { MediaNativeVideoModel } from './media-native-video/media-native-video.model';
import { MediaVimeoModel } from './media-vimeo/media-vimeo.model';
import { MediaYouTubeModel } from './media-youtube/media-youtube.model';
import { MediaModel } from './media.model';
import { MediaTypes } from './media.types';

/**
 * The Default State
 */
function initialState(): MediaModel {
  return {
    width: undefined,
    height: undefined,
    autoplay: false,
    controls: true,
    keyboard: false,
    loop: false,
    playsinline: false,
    poster: undefined,
    muted: false,
    pip: false,
    download: true,
    interaction: true,
    youtube: undefined,
    vimeo: undefined,
    video: undefined,
    audio: undefined,
    configured: false,
    initialized: false,
    intersecting: false,
    hasIntersectedPreviously: false,
    errorIcon: {
      name: 'far:triangle-exclamation',
      size: '2x',
    },
    error: false,
    errorReason: undefined,
    detailsObtained: false,
    explicitPoster: false,
    explicitDimensions: false,
    type: MediaTypes.video,
    ready: false,
    playing: false,
    paused: false,
    ended: false,
    currentTime: 0,
    duration: 0,
    mediaID: 'media',
    hasMediaPlayed: false,
    displayUIOverlay: false,
    preventPauseOnViewportExit: false,
  };
}

/**
 * The state for a {@link MediaComponent}. Provided per component instance and
 * shared by the media orchestrator, the leaf player components and the media UI.
 */
@Injectable()
export class MediaRepository {
  private readonly document = inject(DOCUMENT);
  private readonly loggerService = inject(LoggerService);
  private readonly mediaManagerRepository = inject(MediaManagerRepository);
  private readonly breakpointObserver = inject(BreakpointObserver);

  /**
   * The unique ID of this media instance, used with the {@link MediaManagerService}
   * to determine which media is currently playing globally within the app.
   */
  private readonly instanceID = uniqueId('media-');

  /**
   * The state.
   */
  private readonly state = signal<MediaModel>(initialState());

  ////////////////////////////////////////////////////////////////////
  // INITIALIZE
  ////////////////////////////////////////////////////////////////////

  /**
   * Initializes the state with the provided configuration.
   *
   * @param configuration - The configuration from the server.
   */
  applyConfiguration(configuration?: IMedia) {
    // if there is no configuration then that is an error
    if (!configuration) {
      throw createDigitaServiceError(`MediaStore`, `applyConfiguration`, `No configuration provided but this is required.`, `config`);
    }

    // one of the following must be provided
    let mediaTypeKnown = false;

    // process the configuration depending on the type
    if (configuration.youtube) {
      this.applyInitializeYouTube(configuration);
      mediaTypeKnown = true;
    } else if (configuration.vimeo) {
      this.applyInitializeVimeo(configuration);
      mediaTypeKnown = true;
    } else if (configuration.video) {
      this.applyInitializeNativeVideo(configuration);
      mediaTypeKnown = true;
    } else if (configuration.audio) {
      this.applyInitializeNativeAudio(configuration);
      mediaTypeKnown = true;
    }

    // if the media type is not known then throw an error
    if (!mediaTypeKnown) {
      throw createDigitaServiceError(
        `MediaStore`,
        `applyInitialize`,
        `No "youtube", "vimeo", "video" or "audio" media type provided but one of these is required.`,
        `config`,
      );
    }
  }

  /**
   * Initialize from a YouTube Configuration.
   *
   * @param configuration - the youtube based configuration
   */
  private applyInitializeYouTube(configuration: IMedia) {
    // if no youtube configuration then throw an error.
    if (!configuration?.youtube) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeYouTube',
        'No youtube configuration provided but this is required.',
        'config',
      );
    }

    // if no video id then throw an error.
    if (!configuration.youtube.videoId) {
      this.applyError('No YouTube "videoId" provided but this is required.');
      return;
    }

    // if the configuration comes with an explicit width and height then use that.
    // this allows the configuration to override the OEmbed Request width and height
    // which is useful for portrait videos.
    let explicitDimensions = false;
    let width: number | undefined = undefined;
    let height: number | undefined = undefined;
    const dimensionsAreNumbers = typeof configuration.width === 'number' && typeof configuration.height === 'number';
    const dimensionsAreValid = dimensionsAreNumbers && configuration.width > 0 && configuration.height > 0;
    if (dimensionsAreValid) {
      explicitDimensions = true;
      width = configuration.width;
      height = configuration.height;
    }

    // if the configuration comes with a specific poster then use that.
    let explicitPoster = false;
    const posterIsString = typeof configuration.poster === 'string';
    const posterIsUrl = posterIsString && configuration.poster.length > 0;
    let poster: string | undefined = undefined;
    if (posterIsUrl) {
      explicitPoster = true;
      poster = configuration.poster;
    }

    // interactive mode
    let interaction = true;
    if (configuration.interaction === false) {
      interaction = false;
    }

    // clone the youtube configuration
    const clonedProviderConfig: MediaYouTubeModel = cloneDeep(configuration.youtube);

    // if there is no playerVars then create an empty object
    if (!clonedProviderConfig.playerVars) {
      clonedProviderConfig.playerVars = {};
    }

    // the width and height must be 100% for the YouTube Player
    clonedProviderConfig.width = '100%';
    clonedProviderConfig.height = '100%';

    // if the configuration for autoplay has been set, then display a warning
    if (clonedProviderConfig.playerVars.autoplay === 1 || clonedProviderConfig.playerVars.autoplay === 0) {
      this.warnTopLevelProperty('youtube.playerVars.autoplay', 'autoplay');
    }
    // set the youtube property using the top level provider
    let autoplay = false;
    if (configuration.autoplay === true) {
      clonedProviderConfig.playerVars.autoplay = 1;
      autoplay = true;
    } else {
      clonedProviderConfig.playerVars.autoplay = 0;
      autoplay = false;
    }

    // if the configuration for controls has been set, then display a warning
    if (clonedProviderConfig.playerVars.controls === 1 || clonedProviderConfig.playerVars.controls === 0) {
      this.warnTopLevelProperty('youtube.playerVars.controls', 'controls');
    }
    // set the youtube property using the top level provider
    let controls = true;
    if (configuration.controls === false || interaction === false) {
      clonedProviderConfig.playerVars.controls = 0;
      controls = false;
    } else {
      clonedProviderConfig.playerVars.controls = 1;
      controls = true;
    }

    // if the configuration for disablekb has been set, then display a warning
    if (clonedProviderConfig.playerVars.disablekb === 1 || clonedProviderConfig.playerVars.disablekb === 0) {
      this.warnTopLevelProperty('youtube.playerVars.disablekb', 'keyboard');
    }
    // set the youtube property using the top level provider - keyboard stays enabled
    // only when requested AND the media is interactive (mirrors the Vimeo path; an
    // interaction lock must also lock the keyboard or arrow keys could skip through)
    let keyboard = false;
    if (configuration.keyboard === true && interaction === true) {
      clonedProviderConfig.playerVars.disablekb = 0;
      keyboard = true;
    } else {
      clonedProviderConfig.playerVars.disablekb = 1;
      keyboard = false;
    }

    // force set the enablejsapi
    clonedProviderConfig.playerVars.enablejsapi = 1;

    // if the configuration for list is provided
    if (clonedProviderConfig.playerVars.list) {
      clonedProviderConfig.playerVars.list = undefined;
      this.warnDoNotUseProperty('youtube.playerVars.list');
    }

    // if the configuration for listType is provided
    if (clonedProviderConfig.playerVars.listType) {
      clonedProviderConfig.playerVars.listType = undefined;
      this.warnDoNotUseProperty('youtube.playerVars.listType');
    }

    // if the configuration for playlist is provided
    if (clonedProviderConfig.playerVars.playlist) {
      clonedProviderConfig.playerVars.playlist = undefined;
      this.warnDoNotUseProperty('youtube.playerVars.playlist');
    }

    // if the configuration for loop is provided
    if (clonedProviderConfig.playerVars.loop === 1 || clonedProviderConfig.playerVars.loop === 0) {
      this.warnTopLevelProperty('youtube.playerVars.loop', 'loop');
    }

    // set the youtube property using the top level provider
    let loop = false;
    if (configuration.loop === true) {
      clonedProviderConfig.playerVars.loop = 1;
      clonedProviderConfig.playerVars.playlist = configuration.youtube.videoId;
      loop = true;
    } else {
      clonedProviderConfig.playerVars.loop = 0;
      loop = false;
    }

    // force set the origin
    clonedProviderConfig.playerVars.origin = this.document?.defaultView?.location?.host;

    // if the configuration for playsinline is provided
    if (clonedProviderConfig.playerVars.playsinline === 1 || clonedProviderConfig.playerVars.playsinline === 0) {
      this.warnTopLevelProperty('youtube.playerVars.playsinline', 'playsinline');
    }
    // set the youtube property using the top level provider
    let playsinline = false;
    if (configuration.playsinline === true) {
      clonedProviderConfig.playerVars.playsinline = 1;
      playsinline = true;
    } else {
      clonedProviderConfig.playerVars.playsinline = 0;
      playsinline = false;
    }

    // pip
    const pip = false;

    // download - YouTube embeds never offer a download button
    const download = false;

    // if the configuration for mute is provided
    if (clonedProviderConfig.playerVars.mute === 1 || clonedProviderConfig.playerVars.mute === 0) {
      this.warnTopLevelProperty('youtube.playerVars.mute', 'muted');
    }
    // set the youtube property using the top level provider
    let muted = false;
    if (configuration.muted === true) {
      clonedProviderConfig.playerVars.mute = 1;
      muted = true;
    } else {
      clonedProviderConfig.playerVars.mute = 0;
      muted = false;
    }

    // prevent media pause when not intersecting
    let preventPauseOnViewportExit = false;
    if (configuration.preventPauseOnViewportExit === true) {
      preventPauseOnViewportExit = true;
    }

    // update the state
    this.state.update((state) => {
      const next: MediaModel = {
        ...state,
        autoplay,
        controls,
        keyboard,
        loop,
        playsinline,
        muted,
        interaction,
        pip,
        download,
        type: MediaTypes.youtube,
        configured: true,
        youtube: clonedProviderConfig,
        mediaID: this.instanceID,
        preventPauseOnViewportExit,
        displayUIOverlay: false, // youtube has its own
      };
      if (explicitDimensions) {
        next.explicitDimensions = explicitDimensions;
        next.width = width;
        next.height = height;
      }
      if (explicitPoster) {
        next.explicitPoster = explicitPoster;
        next.poster = poster;
      }
      return next;
    });
  }

  /**
   * Initialize from a Vimeo Configuration.
   *
   * @param configuration - the vimeo based configuration
   */
  private applyInitializeVimeo(configuration: IMedia) {
    // if no vimeo configuration then throw an error.
    if (!configuration.vimeo) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeVimeo',
        'No Vimeo configuration provided but this is required.',
        'config',
      );
    }

    // if no video id OR url then throw an error.
    if (!configuration.vimeo.id && !configuration.vimeo.url) {
      this.applyError('No vimeo "id" or "url" provided but this is required.');
      return;
    }

    // if the configuration comes with an explicit width and height then use that.
    // this allows the configuration to override the OEmbed Request width and height
    // which is useful for portrait videos.
    let explicitDimensions = false;
    let width: number | undefined = undefined;
    let height: number | undefined = undefined;
    const dimensionsAreNumbers = typeof configuration.width === 'number' && typeof configuration.height === 'number';
    const dimensionsAreValid = dimensionsAreNumbers && configuration.width > 0 && configuration.height > 0;
    if (dimensionsAreValid) {
      explicitDimensions = true;
      width = configuration.width;
      height = configuration.height;
    }

    // if the configuration comes with a specific poster then use that.
    let explicitPoster = false;
    const posterIsString = typeof configuration.poster === 'string';
    const posterIsUrl = posterIsString && configuration.poster.length > 0;
    let poster: string | undefined = undefined;
    if (posterIsUrl) {
      explicitPoster = true;
      poster = configuration.poster;
    }

    // interactive mode
    let interaction = true;
    if (configuration.interaction === false) {
      interaction = false;
    }

    // inject required parameters
    const clonedProviderConfig: MediaVimeoModel = cloneDeep(configuration.vimeo);

    // if the configuration for autoplay is provided
    if (clonedProviderConfig.autoplay === true || clonedProviderConfig.autoplay === false) {
      this.warnTopLevelProperty('vimeo.autoplay', 'autoplay');
    }
    let autoplay = false;
    if (configuration.autoplay === true) {
      clonedProviderConfig.autoplay = true;
      autoplay = true;
    } else {
      clonedProviderConfig.autoplay = false;
      autoplay = false;
    }

    // if the configuration for autopause is provided
    if (clonedProviderConfig.autopause === true || clonedProviderConfig.autopause === false) {
      clonedProviderConfig.autopause = undefined;
      this.warnDoNotUseProperty('vimeo.autopause');
    }

    // if the configuration for background is provided
    if (clonedProviderConfig.background === true || clonedProviderConfig.background === false) {
      clonedProviderConfig.background = undefined;
      this.warnDoNotUseProperty('vimeo.background');
    }

    // if the configuration for controls is provided
    if (clonedProviderConfig.controls === true || clonedProviderConfig.controls === false) {
      this.warnTopLevelProperty('vimeo.controls', 'controls');
    }
    // set the vimeo property using the top level provider
    let controls = true;
    if (configuration.controls === false || interaction === false) {
      clonedProviderConfig.controls = false;
      controls = false;
    } else {
      clonedProviderConfig.controls = true;
      controls = true;
    }

    // if the width is provided
    if (clonedProviderConfig.width) {
      clonedProviderConfig.width = undefined;
      this.warnDoNotUseProperty('vimeo.width');
    }

    // if the height is provided
    if (clonedProviderConfig.height) {
      clonedProviderConfig.height = undefined;
      this.warnDoNotUseProperty('vimeo.height');
    }

    // if the configuration for keyboard is provided
    if (clonedProviderConfig.keyboard === true || clonedProviderConfig.keyboard === false) {
      this.warnTopLevelProperty('vimeo.keyboard', 'keyboard');
    }
    // set the vimeo property using the top level provider
    let keyboard = false;
    if (configuration.keyboard === true && interaction === true) {
      clonedProviderConfig.keyboard = true;
      keyboard = true;
    } else {
      clonedProviderConfig.keyboard = false;
      keyboard = false;
    }

    // if loop has been provided
    if (clonedProviderConfig.loop === true || clonedProviderConfig.loop === false) {
      this.warnTopLevelProperty('vimeo.loop', 'loop');
    }
    // set the vimeo property using the top level provider
    let loop = false;
    if (configuration.loop === true) {
      clonedProviderConfig.loop = true;
      loop = true;
    } else {
      clonedProviderConfig.loop = false;
      loop = false;
    }

    // if max width has been provided
    if (clonedProviderConfig.maxwidth) {
      clonedProviderConfig.maxwidth = undefined;
      this.warnDoNotUseProperty('vimeo.maxwidth');
    }

    // if max height has been provided
    if (clonedProviderConfig.maxheight) {
      clonedProviderConfig.maxheight = undefined;
      this.warnDoNotUseProperty('vimeo.maxheight');
    }

    // if the configuration for playsinline is provided
    if (clonedProviderConfig.playsinline === true || clonedProviderConfig.playsinline === false) {
      this.warnTopLevelProperty('vimeo.playsinline', 'playsinline');
    }
    // set the vimeo property using the top level provider
    let playsinline = false;
    if (configuration.playsinline === true) {
      clonedProviderConfig.playsinline = true;
      playsinline = true;
    } else {
      clonedProviderConfig.playsinline = false;
      playsinline = false;
    }

    // the responsive property is hardcoded
    clonedProviderConfig.responsive = true;

    // muted
    if (clonedProviderConfig.muted === true || clonedProviderConfig.muted === false) {
      this.warnTopLevelProperty('vimeo.muted', 'muted');
    }
    let muted = false;
    if (configuration.muted === true) {
      clonedProviderConfig.muted = true;
      muted = true;
    } else {
      clonedProviderConfig.muted = false;
      muted = false;
    }

    // pip
    if (clonedProviderConfig.pip === true || clonedProviderConfig.pip === false) {
      this.warnTopLevelProperty('vimeo.pip', 'pip');
    }
    let pip = false;
    if (configuration.pip === true) {
      clonedProviderConfig.pip = true;
      pip = true;
    } else {
      clonedProviderConfig.pip = false;
      pip = false;
    }

    // download
    if (clonedProviderConfig.download === true || clonedProviderConfig.download === false) {
      this.warnTopLevelProperty('vimeo.download', 'download');
    }
    let download = true;
    if (configuration.download === false) {
      clonedProviderConfig.download = false;
      download = false;
    } else {
      clonedProviderConfig.download = true;
      download = true;
    }

    // prevent media pause when not intersecting
    let preventPauseOnViewportExit = false;
    if (configuration.preventPauseOnViewportExit === true) {
      preventPauseOnViewportExit = true;
    }

    // update the state
    this.state.update((state) => {
      const next: MediaModel = {
        ...state,
        autoplay,
        controls,
        keyboard,
        loop,
        playsinline,
        muted,
        pip,
        download,
        interaction,
        type: MediaTypes.vimeo,
        configured: true,
        vimeo: clonedProviderConfig,
        mediaID: this.instanceID,
        preventPauseOnViewportExit,
        displayUIOverlay: true,
      };
      if (explicitDimensions) {
        next.explicitDimensions = explicitDimensions;
        next.width = width;
        next.height = height;
      }
      if (explicitPoster) {
        next.explicitPoster = explicitPoster;
        next.poster = poster;
      }
      return next;
    });
  }

  /**
   * Initialize from a Video Configuration.
   *
   * @param configuration - the Video based configuration
   */
  private applyInitializeNativeVideo(configuration: IMedia) {
    // if no video configuration then throw an error.
    if (!configuration.video) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeNativeVideo',
        'No video configuration provided but this is required.',
        'config',
      );
    }

    // if no sources then throw an error.
    if (!configuration.video.sources || configuration.video.sources.length === 0) {
      this.applyError('No media "sources" provided but this is required.');
      return;
    }

    // if no width and height then that is an error
    const explicitDimensions = true;
    const dimensionsAreNumbers = typeof configuration.width === 'number' && typeof configuration.height === 'number';
    const dimensionsAreValid = dimensionsAreNumbers && configuration.width > 0 && configuration.height > 0;
    if (!dimensionsAreValid) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeNativeVideo',
        'No media "width" and "height" provided but this is required.',
        'config',
      );
    }
    const width = configuration.width;
    const height = configuration.height;

    // if the configuration comes with a specific poster then use that.
    let explicitPoster = false;
    const posterIsString = typeof configuration.poster === 'string';
    const posterIsUrl = posterIsString && configuration.poster.length > 0;
    let poster: string | undefined = undefined;
    if (posterIsUrl) {
      explicitPoster = true;
      poster = configuration.poster;
    }

    let preload = false;
    if (configuration.video.preload === true) {
      preload = true;
    }

    const sources = configuration.video.sources;

    // interactive mode
    let interaction = true;
    if (configuration.interaction === false) {
      interaction = false;
    }

    // autoplay
    let autoplay = false;
    if (configuration.autoplay === true) {
      autoplay = true;
    }

    // controls
    let controls = true;
    if (configuration.controls === false || interaction === false) {
      controls = false;
    }

    // keyboard
    const keyboard = false;

    // loop
    let loop = false;
    if (configuration.loop === true) {
      loop = true;
    }

    // plays inline
    let playsinline = false;
    if (configuration.playsinline === true) {
      playsinline = true;
    }

    // muted
    let muted = false;
    if (configuration.muted === true) {
      muted = true;
    }

    // pip
    let pip = true;
    if (configuration.pip === false) {
      pip = false;
    }

    // download
    let download = true;
    if (configuration.download === false) {
      download = false;
    }

    // clone the video configuration
    const clonedProviderConfig: MediaNativeVideoModel = {
      preload,
      sources,
      autoplay,
      controls,
      loop,
      playsinline,
      muted,
      pip,
      download,
      poster,
    };

    // prevent media pause when not intersecting
    let preventPauseOnViewportExit = false;
    if (configuration.preventPauseOnViewportExit === true) {
      preventPauseOnViewportExit = true;
    }

    // update the state
    this.state.update((state) => {
      const next: MediaModel = {
        ...state,
        autoplay,
        controls,
        keyboard,
        loop,
        playsinline,
        muted,
        pip,
        download,
        interaction,
        type: MediaTypes.video,
        configured: true,
        video: clonedProviderConfig,
        mediaID: this.instanceID,
        preventPauseOnViewportExit,
        displayUIOverlay: true,
      };
      if (explicitDimensions) {
        next.explicitDimensions = explicitDimensions;
        next.width = width;
        next.height = height;
      }
      if (explicitPoster) {
        next.explicitPoster = explicitPoster;
        next.poster = poster;
      }
      return next;
    });
  }

  /**
   * Initialize from a Audio Configuration.
   *
   * @param configuration - the Audio based configuration
   */
  private applyInitializeNativeAudio(configuration: IMedia) {
    // if no audio configuration then throw an error.
    if (!configuration.audio) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeNativeAudio',
        'No Audio configuration provided but this is required.',
        'config',
      );
    }

    // if no sources then throw an error.
    if (!configuration.audio.sources || configuration.audio.sources.length === 0) {
      this.applyError('No media "sources" provided but this is required.');
      return;
    }

    // if no width and height then that is an error
    const explicitDimensions = true;
    const dimensionsAreNumbers = typeof configuration.width === 'number' && typeof configuration.height === 'number';
    const dimensionsAreValid = dimensionsAreNumbers && configuration.width > 0 && configuration.height > 0;
    if (!dimensionsAreValid) {
      throw createDigitaServiceError(
        'MediaStore',
        'applyInitializeNativeAudio',
        'No media "width" and "height" provided but this is required.',
        'config',
      );
    }
    const width = configuration.width;
    const height = configuration.height;

    // if the configuration comes with a specific poster then use that.
    let explicitPoster = false;
    const posterIsString = typeof configuration.poster === 'string';
    const posterIsUrl = posterIsString && configuration.poster.length > 0;
    let poster: string | undefined = undefined;
    if (posterIsUrl) {
      explicitPoster = true;
      poster = configuration.poster;
    }

    // interactive mode
    let interaction = true;
    if (configuration.interaction === false) {
      interaction = false;
    }

    let preload = false;
    if (configuration.audio.preload === true) {
      preload = true;
    }

    let audioposter: undefined | string = undefined;
    if (configuration.audio.audioposter) {
      audioposter = configuration.audio.audioposter;
    }

    const sources = configuration.audio.sources;

    // autoplay
    let autoplay = false;
    if (configuration.autoplay === true) {
      autoplay = true;
    }

    // controls
    let controls = true;
    if (configuration.controls === false || interaction === false) {
      controls = false;
    }

    // keyboard
    const keyboard = false;

    // loop
    let loop = false;
    if (configuration.loop === true) {
      loop = true;
    }

    // plays inline is not supported on audio
    const playsinline = false;

    // muted
    let muted = false;
    if (configuration.muted === true) {
      muted = true;
    }

    // pip is not supported on audio
    const pip = false;

    // download
    let download = true;
    if (configuration.download === false) {
      download = false;
    }

    // clone the audio configuration
    const clonedProviderConfig: MediaNativeAudioModel = {
      preload,
      audioposter,
      sources,
      autoplay,
      controls,
      loop,
      muted,
      download,
    };

    // prevent media pause when not intersecting
    let preventPauseOnViewportExit = false;
    if (configuration.preventPauseOnViewportExit === true) {
      preventPauseOnViewportExit = true;
    }

    // update the state
    this.state.update((state) => {
      const next: MediaModel = {
        ...state,
        autoplay,
        controls,
        keyboard,
        loop,
        playsinline,
        muted,
        pip,
        download,
        interaction,
        type: MediaTypes.audio,
        configured: true,
        audio: clonedProviderConfig,
        mediaID: this.instanceID,
        preventPauseOnViewportExit,
        displayUIOverlay: true,
      };
      if (explicitDimensions) {
        next.explicitDimensions = explicitDimensions;
        next.width = width;
        next.height = height;
      }
      if (explicitPoster) {
        next.explicitPoster = explicitPoster;
        next.poster = poster;
      }
      return next;
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // STATES
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * The total duration of the video in seconds
   *
   * @param duration - the duration of the video in seconds
   */
  applyDuration(duration: number) {
    this.state.update((state) => ({ ...state, duration }));
  }

  /**
   * The current time of the video in seconds.
   *
   * @param currentTime - the current time of the video in seconds
   */
  applyCurrentTime(currentTime: number) {
    this.state.update((state) => ({ ...state, currentTime }));
  }

  /**
   * The video is ready to play.
   */
  applyReady() {
    this.state.update((state) => ({ ...state, ready: true }));
  }

  /**
   * The video is Playing.
   */
  applyPlaying() {
    this.state.update((state) => ({
      ...state,
      playing: true,
      paused: false,
      ended: false,
      hasMediaPlayed: true,
    }));
  }

  /**
   * The video is Paused.
   */
  applyPaused() {
    this.state.update((state) => ({
      ...state,
      playing: false,
      paused: true,
      ended: false,
    }));
  }

  /**
   * The Video is Ended.
   */
  applyEnded() {
    if (!this.state().loop) {
      this.state.update((state) => ({ ...state, ended: true }));
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // INITIALIZED
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * When the system has been configured, and loaded the required providers and video information (where applicable)
   * then the system is considered initialized and is ready to create the player.
   */
  applyInitialized() {
    this.state.update((state) => ({
      ...state,
      initialized: true,
      hasIntersectedPreviously: state.hasIntersectedPreviously || state.intersecting,
    }));
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // VIDEO DETAILS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Once the video details have been obtained from the YouTube OEmbed Service, it will contain
   * the width and height of the video and the poster.
   *
   * If width, height or poster have been explicitly set, then the values from the OEmbed
   * service will be ignored.
   *
   * @param details - the details of the video
   */
  applyYouTubeVideoDetails(details: YT.OEmbed) {
    this.state.update((state) => ({
      ...state,
      detailsObtained: true,
      poster: state.explicitPoster ? state.poster : details.thumbnail_url,
      width: state.explicitDimensions ? state.width : details.width,
      height: state.explicitDimensions ? state.height : details.height,
    }));
  }

  /**
   * Once the video details have been obtained from the Vimeo OEmbed Service, it will contain
   * the width and height of the video and the poster.
   *
   * If width, height or poster have been explicitly set, then the values from the OEmbed
   * service will be ignored.
   *
   * @param details - the details of the video
   */
  applyVimeoVideoDetails(details: Vimeo.OEmbed) {
    this.state.update((state) => ({
      ...state,
      detailsObtained: true,
      poster: state.explicitPoster ? state.poster : details.thumbnail_url,
      width: state.explicitDimensions ? state.width : details.width,
      height: state.explicitDimensions ? state.height : details.height,
    }));
  }

  /**
   * All Details for a Media Native Video Component have been obtained already.
   */
  applyNativeVideoDetails() {
    this.state.update((state) => ({ ...state, detailsObtained: true }));
  }

  /**
   * All Details for a Media Native Audio Component have been obtained already.
   */
  applyNativeAudioDetails() {
    this.state.update((state) => ({ ...state, detailsObtained: true }));
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // ERROR
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * The system has encountered an error.
   *
   * This can be because:
   *
   *   - the video source failed to load (e.g. youtube video id is invalid)
   *
   * @param reason - the reason for the error
   */
  applyError(reason?: string) {
    if (reason) {
      this.loggerService.error(`MediaComponent - ${reason}`);
    }

    this.state.update((state) => ({
      ...state,
      initialized: false,
      error: true,
      // the user-facing reason is deliberately generic; the real reason goes to the logger
      errorReason: 'Error',
      width: state.width || 640,
      height: state.height || 360,
    }));
  }

  /**
   * Is the component intersecting the viewport?
   *
   * @param isVisible - is the component intersecting?
   */
  applyIsIntersecting(isVisible: boolean) {
    this.state.update((state) => ({
      ...state,
      intersecting: isVisible,
      hasIntersectedPreviously: state.hasIntersectedPreviously || (isVisible && state.initialized),
    }));
  }

  /**
   * Utility
   */
  private warnTopLevelProperty(propertyName: string, topLevelPropertyName: string) {
    this.loggerService.warn(
      `MediaComponent - The ${propertyName} property should not be used. Instead use the top level ${topLevelPropertyName} property of the Media API. Your setting has been ignored.`,
    );
  }

  /**
   * Utility
   */
  private warnDoNotUseProperty(propertyName: string) {
    this.loggerService.warn(
      `MediaComponent - The ${propertyName} property should not be used, it is either not supported or managed by the Media API. Your setting has been ignored.`,
    );
  }

  ////////////////////////////////////////////////////////////////////
  // QUERIES
  ////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // STARTUP & ERRORS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Has the Media Been Configured. This means a configuration has been passed to the
   * component and has been successfully parsed and validated.
   */
  private readonly configured = computed(() => this.state().configured);

  /**
   * What is the type of the Media?
   */
  private readonly type = computed(() => this.state().type);

  /**
   * Has an error occurred?
   */
  readonly error = computed(() => this.state().error);

  /**
   * What is the reason for the error?
   */
  readonly errorReason = computed(() => this.state().errorReason);

  /**
   * The Error Icon to display if things go wrong.
   */
  readonly errorIcon = computed(() => this.state().errorIcon);

  /**
   * Once the Media has stopped initializing it becomes initialized.
   */
  readonly initialized = computed(() => this.state().initialized);

  /**
   * The system is initializing while it is neither initialized nor in error.
   */
  readonly initializing = computed(() => !(this.initialized() || this.error()));

  /**
   * This is true when the system is initializing and an error occurs.
   */
  readonly initializingError = computed(() => !this.initialized() && this.error());

  /**
   * The media type as a stream, for the one-shot initialize sequence.
   */
  private readonly type$ = toObservable(this.type);

  /**
   * Once the Media has been configured, we can get the type from the configuration as it
   * would have been set.
   *
   * The 100ms delay is a long-standing timing shim between configuration (applied
   * during component init) and the downstream initialize sequences; keep it unless
   * its removal is explicitly tested.
   */
  readonly configuredType$ = toObservable(this.configured).pipe(
    delay(100),
    filter((configured) => configured),
    switchMap(() => this.type$),
  );

  //////////////////////////////////////////////////////////////////////////////////
  // PLAYBACK STATUS
  //////////////////////////////////////////////////////////////////////////////////

  // is the media ready to play?
  private readonly isReadyState = computed(() => this.state().ready);

  /**
   * The Media ID of this media. This is used with {@link MediaManagerService} to
   * determine which media is currently playing globally within the app.
   */
  readonly mediaID = computed(() => this.state().mediaID);

  /**
   * Is the media currently playing?
   */
  readonly isPlaying = computed(() => this.state().playing);

  /**
   * Has the media ended?
   */
  readonly ended = computed(() => this.state().ended);

  /**
   * Is the media currently paused? When the media has ended the pause state is
   * suppressed so the media-ui pause animation is not displayed.
   */
  readonly isPaused = computed(() => this.state().paused && !this.ended());

  /**
   * The Duration of the media in seconds.
   *
   * (How long the total length of the media is)
   */
  readonly duration = computed(() => this.state().duration);

  /**
   * The Current Time of the media in seconds.
   *
   * (How much of the video length has passed).
   */
  readonly currentTime = computed(() => this.state().currentTime);

  /**
   * The progress of playback as a percentage.
   */
  readonly progress = computed(() => {
    const currentTime = this.currentTime();
    const duration = this.duration();
    if (currentTime && duration > 0) {
      return Math.round((currentTime / duration) * 100);
    }
    return 0;
  });

  /**
   * The player becomes ready exactly once: when it is initialized and ready without
   * error. The stream completes after the first emission - later errors do not
   * revoke readiness.
   */
  readonly ready$ = toObservable(computed(() => this.initialized() && this.isReadyState() && !this.error())).pipe(
    filter((ready) => ready),
    take(1),
  );

  /**
   * Latched form of {@link ready$} for templates: false until the media becomes
   * ready, then true for the rest of the component's life.
   */
  readonly isReady = toSignal(this.ready$, { initialValue: false });

  //////////////////////////////////////////////////////////////////////////////////
  // CONFIGURED OPTIONS
  //////////////////////////////////////////////////////////////////////////////////

  /**
   * Does the ready media have controls? Used by {@link displayUIControlZone} to decide
   * whether a click-to-play zone is needed on top of the player.
   */
  private readonly controls = computed(() => this.isReadyState() && this.state().controls);

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // MEDIA DETAILS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Have details been obtained? This is primarily used for streaming providers such as
   * YouTube and Vimeo. Because these services require an API call to obtain the details
   * of the media. This is also used for Native Video and Audio but in those cases the
   * value is always true since the data for those is provided upfront during configuration.
   */
  private readonly detailsObtained = computed(() => this.state().detailsObtained);

  /**
   * The Poster source of the Media. This is optional.
   */
  private readonly posterSource = computed(() => this.state().poster);

  /**
   * The Width of the Media.
   */
  private readonly width = computed(() => this.state().width);

  /**
   * The Height of the Media.
   */
  private readonly height = computed(() => this.state().height);

  //////////////////////////////////////////////////////////////////////////////////
  // TEMPLATE CONDITIONALS
  //////////////////////////////////////////////////////////////////////////////////

  /**
   * Is the Media intersecting with the DOM?
   */
  private readonly intersecting = computed(() => this.state().intersecting);

  /**
   * Has the Media previously intersected with the DOM?
   */
  private readonly hasIntersectedPreviously = computed(() => this.state().hasIntersectedPreviously);

  /**
   * Should media be rendered? This occurs if both the following is true:
   *
   *    1. The media is initialized.
   *    2. The media has previously intersected with the DOM.
   */
  private readonly shouldMediaRender = computed(() => this.initialized() && this.hasIntersectedPreviously());

  /**
   * Should the YouTube player be rendered?
   */
  readonly shouldRenderYouTube = computed(() => this.type() === MediaTypes.youtube && this.shouldMediaRender());

  /**
   * Should the Vimeo player be rendered?
   */
  readonly shouldRenderVimeo = computed(() => this.type() === MediaTypes.vimeo && this.shouldMediaRender());

  /**
   * Should the Native Video player be rendered?
   */
  readonly shouldRenderVideo = computed(() => this.type() === MediaTypes.video && this.shouldMediaRender());

  /**
   * Should the Audio player be rendered?
   */
  readonly shouldRenderAudio = computed(() => this.type() === MediaTypes.audio && this.shouldMediaRender());

  /**
   * Aspect ratio of the media. Uses server-supplied width/height when available;
   * falls back to 16/9 (YouTube/Vimeo standard) so the container reserves space
   * before the player boots, preventing layout collapse.
   */
  readonly aspectRatio = computed(() => {
    const width = this.width();
    const height = this.height();
    if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
      return width / height;
    }
    return 16 / 9;
  });

  /**
   * Determine if the Media is Portrait. This is known when:
   *
   *   1. The Media Details have been obtained.
   *   2. The Media Height is greater than the Media Width.
   */
  readonly isMediaPortrait = computed(() => {
    const width = this.width();
    const height = this.height();
    return this.detailsObtained() && typeof width === 'number' && typeof height === 'number' && height > width;
  });

  /**
   * Is the viewport currently at the smallest (phone) breakpoint?
   */
  private readonly isXSmall = toSignal(this.breakpointObserver.observe(Breakpoints.XSmall).pipe(map((result) => result.matches)), {
    initialValue: this.breakpointObserver.isMatched(Breakpoints.XSmall),
  });

  /**
   * Whether the Media should be limited in width on desktop to prevent portrait
   * videos being too big to fit on horizontal screens.
   */
  readonly limitPortraitWidthOnDesktop = computed(() => this.isMediaPortrait() && !this.isXSmall());

  /**
   * Is there a poster for this media? A poster (if available) is only shown once
   * details are obtained and while no error exists.
   */
  readonly hasPoster = computed(() => this.detailsObtained() && !this.error() && !!this.posterSource());

  /**
   * The Poster Image of the Media. Returned as a CSS background image.
   */
  readonly poster = computed(() => (this.hasPoster() ? `url("${this.posterSource()}")` : undefined));

  //////////////////////////////////////////////////////////////////////////////////
  // PROVIDER SETTINGS
  //////////////////////////////////////////////////////////////////////////////////

  /**
   * If the Media is YouTube, the YouTube Settings.
   */
  readonly youtubeSettings = computed(() => (this.type() === MediaTypes.youtube ? this.state().youtube : undefined));

  /**
   * If the Media is Vimeo, the Vimeo Settings.
   */
  readonly vimeoSettings = computed(() => (this.type() === MediaTypes.vimeo ? this.state().vimeo : undefined));

  /**
   * If the Media is a Native Video, the Native Video Settings.
   */
  readonly videoSettings = computed(() => (this.type() === MediaTypes.video ? this.state().video : undefined));

  /**
   * If the Media is a Native Audio, the Native Audio Settings.
   */
  readonly audioSettings = computed(() => (this.type() === MediaTypes.audio ? this.state().audio : undefined));

  /**
   * If it's an Audio Source, the optional playing-state poster.
   */
  readonly audioPoster = computed(() => this.audioSettings()?.audioposter);

  //////////////////////////////////////////////////////////////////////////////////
  // MEDIA UI
  //////////////////////////////////////////////////////////////////////////////////

  /**
   * Have interactions been disabled? The opposite of interactive.
   */
  readonly interactionDisabled = computed(() => !this.state().interaction);

  /**
   * Should drimify custom UI be displayed for this provider? Never the case for
   * YouTube which has its own UI at all times.
   */
  private readonly displayUIOverlayConfigured = computed(() => this.state().displayUIOverlay);

  /**
   * Does the size of the component meet the minimum requirements to display the UI? For example
   * this prevents a thin audio player from being intersected by the UI.
   */
  private readonly displayUIMinimumSize = computed(() => {
    const width = this.width();
    const height = this.height();
    return typeof width === 'number' && typeof height === 'number' && width > 179 && height > 179;
  });

  /**
   * Should the Media UI be displayed?
   */
  readonly displayUIOverlay = computed(() => this.displayUIOverlayConfigured() && this.displayUIMinimumSize());

  /**
   * Has the media previously played?
   */
  private readonly hasMediaPlayed = computed(() => this.state().hasMediaPlayed);

  /**
   * Should the Media UI Play Button be displayed?
   */
  readonly displayUIPlayButton = computed(
    () => this.isReadyState() && this.displayUIOverlay() && !this.hasMediaPlayed() && !this.error() && !this.interactionDisabled(),
  );

  /**
   * Should the Media UI enable a click zone to play the media?
   */
  readonly displayUIControlZone = computed(
    () => !this.error() && this.isReadyState() && this.displayUIOverlay() && !this.controls() && this.state().interaction,
  );

  //////////////////////////////////////////////////////////////////////////////////
  // MEDIA MANAGER
  //////////////////////////////////////////////////////////////////////////////////

  /**
   * When a Media Item is no longer intersecting with the viewport, should that media
   * be paused? By default media pauses when it leaves the viewport; the server can
   * disable that with preventPauseOnViewportExit.
   */
  readonly shouldPauseWhenNoLongerIntersecting = computed(() => {
    // if the media is intersecting then it shouldn't pause
    if (this.intersecting()) {
      return false;
    }

    // if the default pause-on-exit behavior has been disabled then it shouldn't pause
    if (this.state().preventPauseOnViewportExit) {
      return false;
    }

    // otherwise it should.
    return true;
  });

  /**
   * True while this media is ready and playing but a different media is the globally
   * active one - in which case this media should pause.
   */
  readonly pauseAsNotActiveMedia = computed(
    () => this.isReadyState() && this.isPlaying() && this.mediaID() !== this.mediaManagerRepository.activeMediaID(),
  );
}
